import {
  ArrowLeft,
  CreditCard,
  Building,
  AlertTriangle,
  User,
  Globe,
} from 'lucide-react'
import { useMemo, useState, useEffect, useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTravelRule, useUpdateTravelRule } from '@/hooks/useCpn'
import axiosInstance from '@/request/axios'
import { cpnService } from '@/services/cpn'
import {
  CpnRequirementsResponse,
  CpnBeneficiaryRequirement,
  CpnTravelRuleRequirement,
  CpnTravelRule,
  CpnBeneficiaryRequirementType,
} from '@/types/cpn'
import { TransferData } from '@/types/payment'
import {
  type LockedQuoteData,
  type RealSwapQuoteData,
  isRealSwapQuoteData,
  isPreparedQuoteData,
} from '@/types/quote'
import { getAllMetadataFieldsForRoute } from '@/utils/cpnRequirements'
import { logger } from '@/utils/logger'
import { validateMetadataField, MetadataFormData } from '@/utils/validation'

interface BeneficiaryMetadataScreenProps {
  transferData: TransferData
  beneficiaryId: string
  onNext: () => void
  onBack: () => void
}

interface FormErrors {
  [key: string]: string | undefined
}

interface OriginatorFormData {
  ORIGINATOR_NATIONALITY: string
  ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER: string
}

const BeneficiaryMetadataScreen = ({
  transferData,
  beneficiaryId,
  onNext,
  onBack,
}: BeneficiaryMetadataScreenProps) => {
  const [formData, setFormData] = useState<MetadataFormData>({
    ACCOUNT_NUMBER: '',
    ACCOUNT_TYPE: '',
    BANK_NAME: '',
    BANK_CODE: '',
    BANK_COUNTRY: transferData.receiveCountry || '',
    CLABE: '',
    RECIPIENT_ID_NUMBER: '',
    RECIPIENT_PHONE_NUMBER: '',
    SWIFT_CODE: '',
    BENEFICIARY_DATE_OF_BIRTH: '',
    BENEFICIARY_NATIONALITY: '',
    BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER: '',
    BENEFICIARY_PHONE_NUMBER: '',
    BENEFICIARY_EMAIL: '',
    ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER: '',
    ORIGINATOR_NATIONALITY: '',
  })
  const [currentQuoteData, setCurrentQuoteData] = useState<{
    originalQuote?: {
      sourceAmount: number
      destinationAmount: number
      exchangeRate: number
      fees: { amount: number; currency: string; percentage: number }
    }
  } | null>(null)
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isValidatingRequirements, setIsValidatingRequirements] =
    useState(false)
  const [originatorFormData, setOriginatorFormData] =
    useState<OriginatorFormData>({
      ORIGINATOR_NATIONALITY: '',
      ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER: '',
    })
  const [originatorErrors, setOriginatorErrors] = useState<FormErrors>({})
  const [originatorTouched, setOriginatorTouched] = useState<
    Record<string, boolean>
  >({})
  const [requirementsData, setRequirementsData] =
    useState<CpnRequirementsResponse | null>(null)

  // Travel rule hooks for originator data
  const { data: travelRuleData, isLoading: isLoadingTravelRule } =
    useTravelRule()
  const updateTravelRuleMutation = useUpdateTravelRule()

  // Originator form data and state

  // Helper function to get locked quote from storage
  const getLockedQuoteFromStorage = (): LockedQuoteData | null => {
    try {
      const lockedQuoteData = sessionStorage.getItem('lockedQuoteData')
      if (!lockedQuoteData) return null

      const quoteData = JSON.parse(lockedQuoteData) as LockedQuoteData
      return quoteData
    } catch (error) {
      logger.error('Failed to parse locked quote data', error)
      return null
    }
  }

  // Function to check payment requirements using API
  const checkPaymentRequirements = useCallback(
    async (quoteId: string): Promise<void> => {
      setIsValidatingRequirements(true)

      try {
        const requirementsResponse = await cpnService.getPaymentRequirements(
          quoteId,
          beneficiaryId
        )

        setRequirementsData(requirementsResponse)
      } catch (err) {
        logger.error('Failed to check requirements', err)
      } finally {
        setIsValidatingRequirements(false)
      }
    },
    [beneficiaryId]
  )

  // Load stored quote data on component mount
  useEffect(() => {
    const storedData = getLockedQuoteFromStorage()

    // Set current quote data for display
    if (storedData) {
      setCurrentQuoteData(storedData)
    }

    if (storedData && isRealSwapQuoteData(storedData)) {
      // Try to extract requirements data directly from stored quote data
      const realSwapData = storedData as RealSwapQuoteData & {
        requirements?: {
          travelRule: Array<{
            name: string
            type: string
            optional: boolean
            values?: string[]
          }>
          beneficiaryAccount: Array<{
            name: string
            type: string
            optional: boolean
            values?: string[]
          }>
        }
        response?: {
          travelRule: Array<{ name: string; value: string | object }>
          beneficiaryAccount: Array<{ name: string; value: string | object }>
        }
        missing?: {
          travelRule: string[]
          beneficiaryAccount: string[]
        }
      }

      if (realSwapData.requirements) {
        // Requirements are already stored - use them
        const requirementsResponse: CpnRequirementsResponse = {
          paymentRequirements: {
            travelRule: realSwapData.requirements
              .travelRule as CpnTravelRuleRequirement[],
            beneficiaryAccount: realSwapData.requirements
              .beneficiaryAccount as CpnBeneficiaryRequirement[],
          },
          paymentResponse: realSwapData.response || {},
          missingRequirements: {
            travelRule: (realSwapData.missing?.travelRule ||
              []) as CpnTravelRule[],
            beneficiaryAccount: (realSwapData.missing?.beneficiaryAccount ||
              []) as CpnBeneficiaryRequirementType[],
          },
          message: 'Requirements loaded from stored quote',
          status: 'success',
        }
        setRequirementsData(requirementsResponse)
      } else {
        // No requirements stored - fetch them from API
        checkPaymentRequirements(realSwapData.id)
      }

      setCurrentQuoteData(realSwapData)
    } else if (storedData && isPreparedQuoteData(storedData)) {
      // This is prepared quote data - we need to create swap quote first to get requirements
      const preparedData = storedData

      // Create swap quote to get requirements
      const createSwapQuoteForRequirements = async () => {
        setIsValidatingRequirements(true)
        try {
          const swapQuotePayload = {
            ...preparedData.swapQuotePayload,
            beneficiaryRefId: beneficiaryId,
          }

          const swapQuoteResult =
            await cpnService.createSwapQuote(swapQuotePayload)

          // Update stored quote data with the real swap quote response
          const realQuoteData = {
            ...swapQuoteResult,
            originalQuote: preparedData.originalQuote,
            lockedAt: new Date().toISOString(),
            isRealSwapQuote: true,
            swapQuotePayload: preparedData.swapQuotePayload,
          }

          sessionStorage.setItem(
            'lockedQuoteData',
            JSON.stringify(realQuoteData)
          )
          setCurrentQuoteData(realQuoteData)

          // If the swap quote response has requirements, use them
          if (swapQuoteResult.requirements) {
            const requirementsResponse: CpnRequirementsResponse = {
              paymentRequirements:
                swapQuoteResult.requirements as CpnRequirementsResponse['paymentRequirements'],
              paymentResponse: (swapQuoteResult.response || {}) as Record<
                string,
                unknown
              >,
              missingRequirements: {
                travelRule: (swapQuoteResult.missing?.travelRule ||
                  []) as CpnTravelRule[],
                beneficiaryAccount: (swapQuoteResult.missing
                  ?.beneficiaryAccount ||
                  []) as CpnBeneficiaryRequirementType[],
              },
              message: 'Requirements loaded from swap quote',
              status: 'success',
            }
            setRequirementsData(requirementsResponse)
          }
        } catch (error) {
          logger.error('Failed to create swap quote for requirements', error)
        } finally {
          setIsValidatingRequirements(false)
        }
      }

      createSwapQuoteForRequirements()
    }
  }, [checkPaymentRequirements, beneficiaryId])

  // Load existing travel rule data when component mounts or data changes
  useEffect(() => {
    if (travelRuleData?.travelRule?.metadata) {
      const existingData = travelRuleData.travelRule.metadata
      setOriginatorFormData({
        ORIGINATOR_NATIONALITY: existingData.ORIGINATOR_NATIONALITY || '',
        ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER:
          existingData.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER || '',
      })
    }
  }, [travelRuleData])

  // Detect route from transferData first, fallback to session lockedQuoteData
  const { routeCountry, routeMethod } = useMemo(() => {
    let country = transferData?.receiveCountry
    let method = transferData?.paymentMethod as string | undefined

    try {
      const raw = sessionStorage.getItem('lockedQuoteData')
      if ((!country || !method) && raw) {
        const data = JSON.parse(raw)
        const swap = data?.swapQuotePayload || {}
        const original = data?.originalQuote || {}
        country =
          country || swap?.destinationCountry || original?.destinationCountry
        method = method || swap?.paymentMethod || original?.paymentMethod
      }
    } catch (_) {
      // ignore parsing errors
    }

    return { routeCountry: country, routeMethod: method } as {
      routeCountry?: string
      routeMethod?: string
    }
  }, [transferData])

  // Determine which metadata fields are required for this route
  const requiredKeys = useMemo(() => {
    const all = getAllMetadataFieldsForRoute(routeCountry, routeMethod)
    const supported = new Set<keyof MetadataFormData>([
      'ACCOUNT_NUMBER',
      'ACCOUNT_TYPE',
      'BANK_NAME',
      'BANK_CODE',
      'BANK_COUNTRY',
      'CLABE',
      'RECIPIENT_ID_NUMBER',
      'RECIPIENT_PHONE_NUMBER',
      'SWIFT_CODE',
      'BENEFICIARY_DATE_OF_BIRTH',
      'BENEFICIARY_NATIONALITY',
      'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER',
      'BENEFICIARY_PHONE_NUMBER',
      'BENEFICIARY_EMAIL',
    ])
    const filtered = all.filter((k) =>
      supported.has(k as keyof MetadataFormData)
    ) as Array<keyof MetadataFormData>
    return filtered
  }, [routeCountry, routeMethod])

  const requiredSet = useMemo(() => new Set(requiredKeys), [requiredKeys])

  const isFieldRequired = (name: keyof MetadataFormData) =>
    requiredSet.has(name)

  // Check if originator fields are required (check directly from route, not filtered requiredKeys)
  const isOriginatorRequired = useMemo(() => {
    const allFields = getAllMetadataFieldsForRoute(routeCountry, routeMethod)
    const hasOriginatorNationality = allFields.includes(
      'ORIGINATOR_NATIONALITY'
    )
    const hasOriginatorNationalId = allFields.includes(
      'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER'
    )
    return hasOriginatorNationality || hasOriginatorNationalId
  }, [routeCountry, routeMethod])

  // Validation function for originator fields
  const validateOriginatorField = (
    name: keyof OriginatorFormData,
    value: string
  ): string | undefined => {
    if (!value.trim()) return 'This field is required'

    switch (name) {
      case 'ORIGINATOR_NATIONALITY':
        if (value.length !== 2)
          return 'Nationality must be 2 characters (country code)'
        break
      case 'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER':
        if (value.length < 5) return 'ID number is too short'
        if (value.length > 50) return 'ID number is too long'
        break
    }
    return undefined
  }

  // Get available bank names from requirements data
  const bankNameOptions = useMemo(() => {
    const bankNameReq =
      requirementsData?.paymentRequirements.beneficiaryAccount.find(
        (req) => req.name === 'BANK_NAME'
      ) as (CpnBeneficiaryRequirement & { values?: string[] }) | undefined
    return bankNameReq?.values || []
  }, [requirementsData])

  // Check if BANK_NAME has predefined values
  const hasPredefinedBankNames = bankNameOptions.length > 0

  const handleInputChange = (name: keyof MetadataFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Validate on change if field has been touched
    if (touched[name]) {
      const error = validateMetadataField(name, value, isFieldRequired)
      setErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const handleInputBlur = (name: keyof MetadataFormData) => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    const error = validateMetadataField(name, formData[name], isFieldRequired)
    setErrors((prev) => ({ ...prev, [name]: error }))
  }

  // Originator form handlers
  const handleOriginatorInputChange = (
    name: keyof OriginatorFormData,
    value: string
  ) => {
    setOriginatorFormData((prev) => ({ ...prev, [name]: value }))

    // Validate on change if field has been touched
    if (originatorTouched[name]) {
      const error = validateOriginatorField(name, value)
      setOriginatorErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const handleOriginatorInputBlur = (name: keyof OriginatorFormData) => {
    setOriginatorTouched((prev) => ({ ...prev, [name]: true }))
    const error = validateOriginatorField(name, originatorFormData[name])
    setOriginatorErrors((prev) => ({ ...prev, [name]: error }))
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    const newOriginatorErrors: FormErrors = {}
    let isValid = true

    // Validate beneficiary metadata
    ;(Object.keys(formData) as Array<keyof MetadataFormData>).forEach((key) => {
      if (!isFieldRequired(key)) {
        // Clear any previous errors for non-required fields
        newErrors[key] = undefined
        return
      }
      const error = validateMetadataField(key, formData[key], isFieldRequired)
      if (error) {
        newErrors[key] = error
        isValid = false
      }
    })

    // Validate originator data if required
    if (isOriginatorRequired) {
      ;(
        Object.keys(originatorFormData) as Array<keyof OriginatorFormData>
      ).forEach((key) => {
        const error = validateOriginatorField(key, originatorFormData[key])
        if (error) {
          newOriginatorErrors[key] = error
          isValid = false
        }
      })
    }

    setErrors(newErrors)
    setOriginatorErrors(newOriginatorErrors)
    setTouched(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    )
    setOriginatorTouched(
      Object.keys(originatorFormData).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      )
    )
    return isValid
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      // Send only filled metadata keys
      const metadata = Object.fromEntries(
        Object.entries(formData)
          .filter(([_, value]) => value && value.trim())
          .map(([key, value]) => [key, value.trim()])
      )

      // Update beneficiary metadata
      const response = await axiosInstance.put(
        `/cpn/beneficiary-user?user_id=${beneficiaryId}`,
        {
          updateType: 'metadata',
          metadata,
        }
      )

      if (response.status !== 200) {
        throw new Error('Failed to update beneficiary metadata')
      }

      // Update originator data if required
      if (isOriginatorRequired) {
        const originatorMetadata = Object.fromEntries(
          Object.entries(originatorFormData)
            .filter(([_, value]) => value && value.trim())
            .map(([key, value]) => [key, value.trim()])
        )

        await updateTravelRuleMutation.mutateAsync({
          metadata: originatorMetadata,
          beneficiaryUuid: beneficiaryId,
        })
      }

      onNext()
    } catch (error) {
      logger.error('Failed to update metadata', error)
      // Show error to user since metadata is now required
      alert('Failed to save details. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Check if form has any errors or empty required fields
  const hasErrors = (
    Object.entries(errors) as Array<
      [keyof MetadataFormData, string | undefined]
    >
  ).some(([key, error]) => isFieldRequired(key) && !!error)
  const hasEmptyRequired = (
    Object.entries(formData) as Array<[keyof MetadataFormData, string]>
  ).some(([key, value]) => isFieldRequired(key) && !value.trim())

  // Check originator form if required
  const hasOriginatorErrors = Object.values(originatorErrors).some(
    (error) => error
  )
  const hasEmptyOriginatorRequired =
    isOriginatorRequired &&
    Object.values(originatorFormData).some((value) => !value.trim())

  const canSubmit =
    !hasErrors &&
    !hasEmptyRequired &&
    !hasOriginatorErrors &&
    !hasEmptyOriginatorRequired &&
    !updateTravelRuleMutation.isPending

  // Determine which sections should render based on required fields
  const showAccountSection = [
    'ACCOUNT_NUMBER',
    'ACCOUNT_TYPE',
    'RECIPIENT_ID_NUMBER',
    'RECIPIENT_PHONE_NUMBER',
    'CLABE',
  ].some((k) => isFieldRequired(k as keyof MetadataFormData))
  const showBankSection = [
    'BANK_NAME',
    'BANK_CODE',
    'BANK_COUNTRY',
    'SWIFT_CODE',
  ].some((k) => isFieldRequired(k as keyof MetadataFormData))
  const showBeneficiarySection = [
    'BENEFICIARY_DATE_OF_BIRTH',
    'BENEFICIARY_NATIONALITY',
    'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER',
    'BENEFICIARY_PHONE_NUMBER',
    'BENEFICIARY_EMAIL',
  ].some((k) => isFieldRequired(k as keyof MetadataFormData))

  // Check if user has made any changes to the form
  const hasUnsavedChanges = () => {
    const hasMetadataChanges = Object.entries(formData).some(([key, value]) => {
      // Skip BANK_COUNTRY as it's pre-filled with transferData.receiveCountry
      if (key === 'BANK_COUNTRY') return false
      return value.trim() !== ''
    })

    const hasOriginatorChanges =
      isOriginatorRequired &&
      Object.values(originatorFormData).some((value) => value.trim() !== '')

    return hasMetadataChanges || hasOriginatorChanges
  }

  // Handle back button with confirmation if there are unsaved changes
  const handleBackClick = () => {
    if (hasUnsavedChanges()) {
      setShowDiscardDialog(true)
    } else {
      onBack()
    }
  }

  // Confirm discard and go back
  const handleConfirmDiscard = () => {
    setShowDiscardDialog(false)
    onBack()
  }

  // Cancel discard dialog
  const handleCancelDiscard = () => {
    setShowDiscardDialog(false)
  }

  return (
    <div className="flex flex-col h-[600px] dark:bg-gray-900">
      <div className="p-4 space-y-4 flex-1 overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackClick}
            className="p-1"
          >
            <ArrowLeft className="w-5 h-5 dark:text-gray-300" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Details Required
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Please provide all details to complete the transfer
            </p>
          </div>
        </div>

        {/* Transfer Summary */}
        <Card className="p-3 bg-blue-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600 dark:text-gray-300">
              Sending to {transferData.receiveCountry}
            </span>
            <span className="font-medium dark:text-white">
              {currentQuoteData?.originalQuote
                ? `${currentQuoteData.originalQuote.sourceAmount} ${transferData.sendCurrency} → ${currentQuoteData.originalQuote.destinationAmount.toLocaleString()} ${transferData.receiveCurrency}`
                : `${transferData.sendAmount} ${transferData.sendCurrency} → ${transferData.receiveAmount} ${transferData.receiveCurrency}`}
            </span>
          </div>
        </Card>

        {/* Requirements Check */}
        {isValidatingRequirements && (
          <Card className="p-4 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Validating payment requirements...
              </span>
            </div>
          </Card>
        )}

        {/* Account Information */}
        {showAccountSection && (
          <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-3">
              <CreditCard className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <h3 className="font-medium text-gray-900 dark:text-white">
                Account Information
              </h3>
            </div>

            <div className="space-y-3">
              {isFieldRequired('ACCOUNT_NUMBER') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Account Number *
                  </label>
                  <Input
                    value={formData.ACCOUNT_NUMBER}
                    onChange={(e) =>
                      handleInputChange('ACCOUNT_NUMBER', e.target.value)
                    }
                    onBlur={() => handleInputBlur('ACCOUNT_NUMBER')}
                    placeholder="e.g., 1234567890"
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.ACCOUNT_NUMBER ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.ACCOUNT_NUMBER && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.ACCOUNT_NUMBER}
                    </p>
                  )}
                </div>
              )}

              {isFieldRequired('ACCOUNT_TYPE') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Account Type *
                  </label>
                  <Input
                    value={formData.ACCOUNT_TYPE}
                    onChange={(e) =>
                      handleInputChange('ACCOUNT_TYPE', e.target.value)
                    }
                    onBlur={() => handleInputBlur('ACCOUNT_TYPE')}
                    placeholder="e.g., Savings, Current, Checking"
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.ACCOUNT_TYPE ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.ACCOUNT_TYPE && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.ACCOUNT_TYPE}
                    </p>
                  )}
                </div>
              )}

              {isFieldRequired('RECIPIENT_ID_NUMBER') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Recipient ID Number *
                  </label>
                  <Input
                    value={formData.RECIPIENT_ID_NUMBER}
                    onChange={(e) =>
                      handleInputChange('RECIPIENT_ID_NUMBER', e.target.value)
                    }
                    onBlur={() => handleInputBlur('RECIPIENT_ID_NUMBER')}
                    placeholder="e.g., National ID, Passport number"
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.RECIPIENT_ID_NUMBER ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.RECIPIENT_ID_NUMBER && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.RECIPIENT_ID_NUMBER}
                    </p>
                  )}
                </div>
              )}

              {isFieldRequired('RECIPIENT_PHONE_NUMBER') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Recipient Phone Number *
                  </label>
                  <Input
                    value={formData.RECIPIENT_PHONE_NUMBER}
                    onChange={(e) =>
                      handleInputChange(
                        'RECIPIENT_PHONE_NUMBER',
                        e.target.value
                      )
                    }
                    onBlur={() => handleInputBlur('RECIPIENT_PHONE_NUMBER')}
                    placeholder="e.g., +1234567890"
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.RECIPIENT_PHONE_NUMBER ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.RECIPIENT_PHONE_NUMBER && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.RECIPIENT_PHONE_NUMBER}
                    </p>
                  )}
                </div>
              )}

              {isFieldRequired('CLABE') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    CLABE Number *
                  </label>
                  <Input
                    type="tel"
                    value={formData.CLABE}
                    onChange={(e) => {
                      // Only allow digits
                      const value = e.target.value.replace(/\D/g, '')
                      handleInputChange('CLABE', value)
                    }}
                    onBlur={() => handleInputBlur('CLABE')}
                    placeholder="e.g., 012345678901234567"
                    maxLength={18}
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.CLABE ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Must be exactly 18 digits
                  </p>
                  {errors.CLABE && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.CLABE}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Bank Information */}
        {showBankSection && (
          <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-3">
              <Building className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <h3 className="font-medium text-gray-900 dark:text-white">
                Bank Information
              </h3>
            </div>

            <div className="space-y-3">
              {isFieldRequired('BANK_NAME') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bank Name *
                  </label>
                  {hasPredefinedBankNames ? (
                    <Select
                      value={formData.BANK_NAME}
                      onValueChange={(value) => {
                        handleInputChange('BANK_NAME', value)
                        setTouched((prev) => ({ ...prev, BANK_NAME: true }))
                      }}
                    >
                      <SelectTrigger
                        className={`w-full dark:bg-gray-900 dark:border-gray-600 dark:text-white ${
                          errors.BANK_NAME
                            ? 'border-red-500 dark:border-red-400'
                            : touched.BANK_NAME && !errors.BANK_NAME
                              ? 'border-green-500 dark:border-green-400'
                              : ''
                        }`}
                      >
                        <SelectValue placeholder="Select bank name" />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-gray-900 dark:border-gray-600 max-h-[300px]">
                        {bankNameOptions.map((bankName: string) => (
                          <SelectItem
                            key={bankName}
                            value={bankName}
                            className="dark:text-white dark:focus:bg-gray-700 dark:focus:text-white text-sm"
                          >
                            {bankName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={formData.BANK_NAME}
                      onChange={(e) =>
                        handleInputChange('BANK_NAME', e.target.value)
                      }
                      onBlur={() => handleInputBlur('BANK_NAME')}
                      placeholder="e.g., HSBC, Bank of America"
                      className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.BANK_NAME ? 'border-red-500 dark:border-red-500' : ''}`}
                    />
                  )}
                  {errors.BANK_NAME && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.BANK_NAME}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {isFieldRequired('BANK_CODE') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Bank Code *
                    </label>
                    <Input
                      value={formData.BANK_CODE}
                      onChange={(e) =>
                        handleInputChange('BANK_CODE', e.target.value)
                      }
                      onBlur={() => handleInputBlur('BANK_CODE')}
                      placeholder="e.g., 004, 123"
                      className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.BANK_CODE ? 'border-red-500 dark:border-red-500' : ''}`}
                    />
                    {errors.BANK_CODE && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {errors.BANK_CODE}
                      </p>
                    )}
                  </div>
                )}

                {isFieldRequired('BANK_COUNTRY') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Bank Country *
                    </label>
                    <Input
                      value={formData.BANK_COUNTRY}
                      onChange={(e) =>
                        handleInputChange(
                          'BANK_COUNTRY',
                          e.target.value.toUpperCase()
                        )
                      }
                      onBlur={() => handleInputBlur('BANK_COUNTRY')}
                      placeholder="HK"
                      maxLength={2}
                      className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.BANK_COUNTRY ? 'border-red-500 dark:border-red-500' : ''}`}
                    />
                    {errors.BANK_COUNTRY && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {errors.BANK_COUNTRY}
                      </p>
                    )}
                  </div>
                )}
              </div>
              {isFieldRequired('SWIFT_CODE') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    SWIFT Code *
                  </label>
                  <Input
                    value={formData.SWIFT_CODE}
                    onChange={(e) =>
                      handleInputChange(
                        'SWIFT_CODE',
                        e.target.value.toUpperCase()
                      )
                    }
                    onBlur={() => handleInputBlur('SWIFT_CODE')}
                    placeholder="e.g., HKBAHKHHXXX"
                    maxLength={11}
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.SWIFT_CODE ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.SWIFT_CODE && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.SWIFT_CODE}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Personal Information (Originator) */}
        {isOriginatorRequired && (
          <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-3">
              <Globe className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <h3 className="font-medium text-gray-900 dark:text-white">
                Personal Information
              </h3>
            </div>

            <div className="space-y-3">
              <div>
                <Label
                  htmlFor="originatorNationality"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Your Nationality *
                </Label>
                <Input
                  id="originatorNationality"
                  value={originatorFormData.ORIGINATOR_NATIONALITY}
                  onChange={(e) =>
                    handleOriginatorInputChange(
                      'ORIGINATOR_NATIONALITY',
                      e.target.value.toUpperCase()
                    )
                  }
                  onBlur={() =>
                    handleOriginatorInputBlur('ORIGINATOR_NATIONALITY')
                  }
                  placeholder="e.g., CA, US, IN"
                  maxLength={2}
                  className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${originatorErrors.ORIGINATOR_NATIONALITY ? 'border-red-500 dark:border-red-500' : ''}`}
                  disabled={isSubmitting || updateTravelRuleMutation.isPending}
                />
                {originatorErrors.ORIGINATOR_NATIONALITY && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {originatorErrors.ORIGINATOR_NATIONALITY}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="originatorNationalId"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Your National ID Number *
                </Label>
                <Input
                  id="originatorNationalId"
                  value={
                    originatorFormData.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER
                  }
                  onChange={(e) =>
                    handleOriginatorInputChange(
                      'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER',
                      e.target.value
                    )
                  }
                  onBlur={() =>
                    handleOriginatorInputBlur(
                      'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER'
                    )
                  }
                  placeholder="Enter your national ID number"
                  className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${originatorErrors.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER ? 'border-red-500 dark:border-red-500' : ''}`}
                  disabled={isSubmitting || updateTravelRuleMutation.isPending}
                />
                {originatorErrors.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {originatorErrors.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER}
                  </p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Beneficiary Information */}
        {showBeneficiarySection && (
          <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-3">
              <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <h3 className="font-medium text-gray-900 dark:text-white">
                Beneficiary Information
              </h3>
            </div>

            <div className="space-y-3">
              {isFieldRequired('BENEFICIARY_DATE_OF_BIRTH') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date of Birth *
                  </label>
                  <Input
                    type="date"
                    value={formData.BENEFICIARY_DATE_OF_BIRTH}
                    onChange={(e) =>
                      handleInputChange(
                        'BENEFICIARY_DATE_OF_BIRTH',
                        e.target.value
                      )
                    }
                    onBlur={() => handleInputBlur('BENEFICIARY_DATE_OF_BIRTH')}
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.BENEFICIARY_DATE_OF_BIRTH ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.BENEFICIARY_DATE_OF_BIRTH && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.BENEFICIARY_DATE_OF_BIRTH}
                    </p>
                  )}
                </div>
              )}

              {isFieldRequired('BENEFICIARY_NATIONALITY') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nationality *
                  </label>
                  <Input
                    value={formData.BENEFICIARY_NATIONALITY}
                    onChange={(e) =>
                      handleInputChange(
                        'BENEFICIARY_NATIONALITY',
                        e.target.value.toUpperCase()
                      )
                    }
                    onBlur={() => handleInputBlur('BENEFICIARY_NATIONALITY')}
                    placeholder="e.g., US, IN"
                    maxLength={2}
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.BENEFICIARY_NATIONALITY ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.BENEFICIARY_NATIONALITY && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.BENEFICIARY_NATIONALITY}
                    </p>
                  )}
                </div>
              )}

              {isFieldRequired(
                'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER'
              ) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    National Identification Number *
                  </label>
                  <Input
                    value={formData.BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER}
                    onChange={(e) =>
                      handleInputChange(
                        'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER',
                        e.target.value
                      )
                    }
                    onBlur={() =>
                      handleInputBlur(
                        'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER'
                      )
                    }
                    placeholder="e.g., National ID, Passport number"
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER}
                    </p>
                  )}
                </div>
              )}

              {isFieldRequired('BENEFICIARY_PHONE_NUMBER') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone Number *
                  </label>
                  <Input
                    value={formData.BENEFICIARY_PHONE_NUMBER}
                    onChange={(e) =>
                      handleInputChange(
                        'BENEFICIARY_PHONE_NUMBER',
                        e.target.value
                      )
                    }
                    onBlur={() => handleInputBlur('BENEFICIARY_PHONE_NUMBER')}
                    placeholder="e.g., +1234567890"
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.BENEFICIARY_PHONE_NUMBER ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.BENEFICIARY_PHONE_NUMBER && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.BENEFICIARY_PHONE_NUMBER}
                    </p>
                  )}
                </div>
              )}

              {isFieldRequired('BENEFICIARY_EMAIL') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={formData.BENEFICIARY_EMAIL}
                    onChange={(e) =>
                      handleInputChange('BENEFICIARY_EMAIL', e.target.value)
                    }
                    onBlur={() => handleInputBlur('BENEFICIARY_EMAIL')}
                    placeholder="e.g., email@example.com"
                    className={`dark:bg-gray-900 dark:border-gray-600 dark:text-white ${errors.BENEFICIARY_EMAIL ? 'border-red-500 dark:border-red-500' : ''}`}
                  />
                  {errors.BENEFICIARY_EMAIL && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {errors.BENEFICIARY_EMAIL}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Action Buttons */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-700 dark:bg-gray-900 space-y-2">
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className="w-full bg-blue-600 hover:bg-gray-800 text-white h-10 text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting || updateTravelRuleMutation.isPending ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving Details...</span>
            </div>
          ) : (
            `Save ${isOriginatorRequired ? 'All' : 'Bank'} Details`
          )}
        </Button>
      </div>

      {/* Discard Confirmation Dialog */}
      {showDiscardDialog && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl p-6 mx-4">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center mx-auto">
                <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>

              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Discard Bank Details?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  You have unsaved bank details. Are you sure you want to go
                  back? All entered information will be lost.
                </p>
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={handleCancelDiscard}
                  variant="outline"
                  className="flex-1"
                >
                  Continue Editing
                </Button>
                <Button
                  onClick={handleConfirmDiscard}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Discard Details
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

export default BeneficiaryMetadataScreen
